/**
 * 🛡️ FIRESTORE WRITE GUARD
 * 
 * THIS IS THE ONLY WAY TO WRITE TO FIRESTORE IN THIS APPLICATION.
 * Direct use of setDoc, updateDoc, addDoc, deleteDoc is FORBIDDEN.
 * 
 * This module enforces:
 * 1. Correct paths (company-scoped under companies/{companyId}/)
 * 2. Required fields (companyId, createdAt, createdBy)
 * 3. Production entry IDs are YYYYMMDD format
 * 4. companyId immutability (can't change after creation)
 * 
 * SCHEMA STANDARD (Dec 2025):
 * - companyId is the ONLY company identifier (no more companyId or companyId)
 * - companyId is slug format (e.g., "ac-oil-gas")
 * 
 * @version 2.0.0
 * @description Schema enforcement at runtime - makes it impossible to write wrong data
 */

import { 
  doc, 
  setDoc, 
  updateDoc, 
  deleteDoc, 
  serverTimestamp,
  DocumentReference
} from 'firebase/firestore';
import { db } from './client';
import { FIRESTORE_PATHS } from './paths';
import { getCompanyId } from '../tenantContext';
import { isoToEntryDateId } from '../dateUtils';

// ============================================
// ERROR CLASSES
// ============================================

export class SchemaViolationError extends Error {
  constructor(message: string, public details?: Record<string, unknown>) {
    super(`🚨 SCHEMA VIOLATION: ${message}`);
    this.name = 'SchemaViolationError';
  }
}

export class TenantMismatchError extends Error {
  constructor(expected: string, received: string) {
    super(`🚨 TENANT MISMATCH: Expected '${expected}', got '${received}'`);
    this.name = 'TenantMismatchError';
  }
}

export class InvalidPathError extends Error {
  constructor(path: string, reason: string) {
    super(`🚨 INVALID PATH: '${path}' - ${reason}`);
    this.name = 'InvalidPathError';
  }
}

// ============================================
// VALIDATION FUNCTIONS
// ============================================

/**
 * Validates that a path starts with companies/{companyId}/
 */
function validateTenantPath(path: string, expectedCompanyId: string): void {
  const expectedPrefix = `companies/${expectedCompanyId}/`;
  
  // Allow root companies path for company document itself
  if (path === `companies/${expectedCompanyId}`) {
    return;
  }
  
  if (!path.startsWith(expectedPrefix)) {
    throw new InvalidPathError(path, `Must start with '${expectedPrefix}'`);
  }
}

/**
 * Validates required base fields exist
 * NOTE: Only companyId is required now (companyId and companyId removed Dec 2025)
 */
function validateRequiredFields(data: Record<string, unknown>, companyId: string, isCreate: boolean): void {
  const errors: string[] = [];
  
  if (isCreate) {
    // companyId is the ONLY company identifier field
    if (!data.companyId) errors.push('Missing companyId');
    
    // Validate companyId matches expected
    if (data.companyId && data.companyId !== companyId) {
      throw new TenantMismatchError(companyId, data.companyId as string);
    }
  } else {
    // On update, prevent changing companyId
    if (data.companyId !== undefined) {
      errors.push('Cannot modify companyId on update - immutable field');
    }
  }
  
  if (errors.length > 0) {
    throw new SchemaViolationError(errors.join(', '), { data, companyId });
  }
}

/**
 * Validates production entry document ID is YYYYMMDD format
 */
function validateProductionEntryId(path: string, docId: string): void {
  if (path.includes('/production_entries/')) {
    if (!/^\d{8}$/.test(docId)) {
      throw new SchemaViolationError(
        `Production entry ID must be YYYYMMDD format (e.g., '20251223'), got '${docId}'`,
        { path, docId }
      );
    }
  }
}

// ============================================
// GUARDED WRITE FUNCTIONS
// ============================================

interface WriteOptions {
  /** Skip tenant validation (for SuperAdmin operations only) */
  skipTenantValidation?: boolean;
  /** User ID performing the action */
  userId?: string;
  /** Merge with existing document */
  merge?: boolean;
}

/**
 * 🛡️ GUARDED CREATE - The ONLY way to create documents
 * 
 * Enforces:
 * - Path must be tenant-scoped
 * - companyId, companyId, companyId must match
 * - createdAt and updatedAt are auto-added
 * - Production entries must have YYYYMMDD ID
 */
export async function guardedCreate<T extends Record<string, unknown>>(
  path: string,
  docId: string,
  data: T,
  options: WriteOptions = {}
): Promise<DocumentReference> {
  const companyId = getCompanyId();
  
  // Validate path
  if (!options.skipTenantValidation) {
    validateTenantPath(path, companyId);
  }
  
  // Validate production entry ID format
  validateProductionEntryId(path, docId);
  
  // Build document with required fields
  // NOTE: Only companyId now - companyId and companyId removed (Dec 2025)
  const docData = {
    ...data,
    id: docId,
    companyId: companyId,
    createdAt: serverTimestamp(),
    updatedAt: serverTimestamp(),
    ...(options.userId && { createdBy: options.userId }),
  };
  
  // Validate required fields
  validateRequiredFields(docData, companyId, true);
  
  // Write to Firestore
  const docRef = doc(db, path, docId);
  await setDoc(docRef, docData, { merge: options.merge });
  
  console.log(`✅ Created: ${path}/${docId}`);
  return docRef;
}

/**
 * 🛡️ GUARDED UPDATE - The ONLY way to update documents
 * 
 * Enforces:
 * - Path must be tenant-scoped
 * - Cannot modify companyId, companyId, companyId
 * - updatedAt is auto-added
 */
export async function guardedUpdate<T extends Record<string, unknown>>(
  path: string,
  docId: string,
  data: T,
  options: WriteOptions = {}
): Promise<void> {
  const companyId = getCompanyId();
  
  // Validate path
  if (!options.skipTenantValidation) {
    validateTenantPath(path, companyId);
  }
  
  // Prevent tenant field modification
  validateRequiredFields(data, companyId, false);
  
  // Add updatedAt
  const updateData = {
    ...data,
    updatedAt: serverTimestamp(),
    ...(options.userId && { updatedBy: options.userId }),
  };
  
  // Write to Firestore
  const docRef = doc(db, path, docId);
  await updateDoc(docRef, updateData);
  
  console.log(`✅ Updated: ${path}/${docId}`);
}

/**
 * 🛡️ GUARDED DELETE - The ONLY way to delete documents
 * 
 * Enforces:
 * - Path must be tenant-scoped
 * - Logs deletion for audit
 */
export async function guardedDelete(
  path: string,
  docId: string,
  options: WriteOptions = {}
): Promise<void> {
  const companyId = getCompanyId();
  
  // Validate path
  if (!options.skipTenantValidation) {
    validateTenantPath(path, companyId);
  }
  
  const docRef = doc(db, path, docId);
  await deleteDoc(docRef);
  
  console.log(`🗑️ Deleted: ${path}/${docId}`);
}

// ============================================
// CONVENIENCE FUNCTIONS (Use these!)
// ============================================

/**
 * Create a user document
 */
export async function createUser(
  companyId: string,
  userId: string,
  userData: Record<string, unknown>,
  creatorId?: string
): Promise<DocumentReference> {
  return guardedCreate(
    FIRESTORE_PATHS.users(companyId),
    userId,
    { ...userData, uid: userId },
    { userId: creatorId }
  );
}

/**
 * Create a well document
 */
export async function createWell(
  companyId: string,
  wellId: string,
  wellData: Record<string, unknown>,
  creatorId?: string
): Promise<DocumentReference> {
  return guardedCreate(
    FIRESTORE_PATHS.wells(companyId),
    wellId,
    wellData,
    { userId: creatorId }
  );
}

/**
 * Create a production entry (ID must be YYYYMMDD or ISO date)
 */
export async function createProductionEntry(
  companyId: string,
  wellId: string,
  entryDate: string, // ISO format YYYY-MM-DD or YYYYMMDD
  entryData: Record<string, unknown>,
  creatorId?: string
): Promise<DocumentReference> {
  // Convert ISO to YYYYMMDD if needed
  const dateId = entryDate.includes('-') ? isoToEntryDateId(entryDate) : entryDate;
  
  return guardedCreate(
    FIRESTORE_PATHS.wellProductionEntries(companyId, wellId),
    dateId,
    { ...entryData, entryDate: entryDate.includes('-') ? entryDate : undefined, wellId },
    { userId: creatorId }
  );
}

/**
 * Update a production entry
 */
export async function updateProductionEntry(
  companyId: string,
  wellId: string,
  dateId: string,
  entryData: Record<string, unknown>,
  updaterId?: string
): Promise<void> {
  return guardedUpdate(
    FIRESTORE_PATHS.wellProductionEntries(companyId, wellId),
    dateId,
    entryData,
    { userId: updaterId }
  );
}

/**
 * Create a vendor document
 */
export async function createVendor(
  companyId: string,
  vendorId: string,
  vendorData: Record<string, unknown>,
  creatorId?: string
): Promise<DocumentReference> {
  return guardedCreate(
    FIRESTORE_PATHS.vendors(companyId),
    vendorId,
    vendorData,
    { userId: creatorId }
  );
}

/**
 * Create a service ticket
 */
export async function createTicket(
  companyId: string,
  ticketId: string,
  ticketData: Record<string, unknown>,
  creatorId?: string
): Promise<DocumentReference> {
  return guardedCreate(
    FIRESTORE_PATHS.tickets(companyId),
    ticketId,
    ticketData,
    { userId: creatorId }
  );
}

/**
 * Create a user invitation
 */
export async function createUserInvitation(
  companyId: string,
  invitationId: string,
  invitationData: Record<string, unknown>,
  creatorId?: string
): Promise<DocumentReference> {
  return guardedCreate(
    FIRESTORE_PATHS.userInvitations(companyId),
    invitationId,
    invitationData,
    { userId: creatorId }
  );
}

/**
 * Create a vendor invitation
 */
export async function createVendorInvitation(
  companyId: string,
  invitationId: string,
  invitationData: Record<string, unknown>,
  creatorId?: string
): Promise<DocumentReference> {
  return guardedCreate(
    FIRESTORE_PATHS.vendorInvitations(companyId),
    invitationId,
    invitationData,
    { userId: creatorId }
  );
}

// ============================================
// All error classes and functions are exported at declaration
// ============================================
